import { QueryClient } from "@tanstack/react-query";
import {
  LoaderFunctionArgs,
  redirect,
  replace,
  useLoaderData,
} from "react-router";

import { orgStorage } from "@/shared/auth";
import { SearchParams } from "@/shared/environment/SearchParams";
import { RobotoUnauthorizedException } from "@/shared/http";
import { makeAuthenticatedHttpClient } from "@/shared/http/makeAuthenticatedHttpClient";
import { Goto } from "@/shared/navigation/Goto";
import { layoutNavigationState } from "@/shared/navigation/layoutNavigation";
import { ErrorMonitoringService } from "@/shared/services";
import { Datasource } from "@/shared/state/visualization";

import {
  EventResolution,
  eventDatasources,
  eventNotices,
  eventSidebarFor,
  resolveEvent,
  resolveEventPanels,
} from "./eventContribution";
import { UnrecoverableError } from "./exceptions";
import {
  resolveByDefaultLayout,
  resolveByLayoutFromNavigationState,
  resolveByLayoutId,
  resolveByLayoutName,
  resolveByWorkspaceId,
} from "./resolvers";
import {
  ConfigSource,
  VisualizerInitContext,
  VisualizerInitData,
  VisualizerNotice,
} from "./visualizerInit";

/**
 * react-router loader for the /visualize route. Resolves the workspace's
 * config source (a saved workspace, a layout, or nothing), the datasources to
 * load, the sidebar view the linked event opens, and any banners to raise.
 *
 * @link https://reactrouter.com/6.30.3/route/loader
 */
export const loader =
  (queryClient: QueryClient) =>
  async ({ request, params }: LoaderFunctionArgs): Promise<Response> => {
    const url = new URL(request.url);

    const httpClient = await makeAuthenticatedHttpClient();
    const orgRecord = orgStorage.getCurrentOrg();

    // Redirect user to select orgs if org not found
    if (!orgRecord) {
      return redirect(`${Goto.route.SignIn}?chooseorg=true`);
    }

    const eventId = url.searchParams.get(SearchParams.EVENT) ?? undefined;

    // A restored workspace carries its own datasources and panels, so fetching
    // the event would only produce content to discard. The event id still
    // reaches assembleInitData, which raises a notice that it was not applied.
    // Sessions are not fetched here at all — the URL's `?session=` becomes a
    // session datasource the fetcher expands, so the loader stays fast.
    const restoringWorkspace = params.workspaceId !== undefined;

    const ctx: VisualizerInitContext = {
      eventId: restoringWorkspace ? undefined : eventId,
      httpClient,
      layoutFromNavState: layoutNavigationState.get(),
      layoutId: url.searchParams.get(SearchParams.LAYOUT_ID) ?? undefined,
      layoutName: url.searchParams.get(SearchParams.LAYOUT) ?? undefined,
      orgId: orgRecord.org_id,
      queryClient,
      workspaceId: params.workspaceId,
    };

    try {
      const eventResolution = await resolveEventSafely(ctx);

      const configSource = await resolveConfigSource(ctx, eventResolution);

      // A v2 workspace's saved playhead becomes `?t=` unless the URL already
      // names one; the redirect re-runs the loader with the param in place.
      // `replace`, not `redirect`: on a full-page load (the path every
      // roboto://workspace link takes) a push would leave the bare URL in
      // history, and Back would land on it only to redirect forward again.
      if (
        configSource?.type === "layout" &&
        configSource.origin === "workspace-v2" &&
        configSource.timestamp !== undefined &&
        !url.searchParams.has(SearchParams.TIMESTAMP)
      ) {
        const params = new URLSearchParams(url.searchParams);
        params.set(SearchParams.TIMESTAMP, configSource.timestamp);
        return replace(`${url.pathname}?${params.toString()}`);
      }

      const urlDatasources = readUrlDatasources(url.searchParams);

      return Response.json(
        assembleInitData(ctx, configSource, {
          eventId,
          eventResolution,
          urlDatasources,
        }),
      );
    } catch (error) {
      if (error instanceof UnrecoverableError) {
        // Let the error boundary handle this error
        throw error;
      }

      if (error instanceof RobotoUnauthorizedException) {
        // User is authenticated but lacks permission to view this resource
        return redirect(`${Goto.route.Unauthorized}`);
      }

      throw error;
    }
  };

/**
 * Resolve the event named in the URL, treating an unexpected fetch failure as
 * "no event": a transient error loading the linked event must not stop the
 * workspace from opening.
 */
async function resolveEventSafely(
  ctx: VisualizerInitContext,
): Promise<EventResolution | null> {
  try {
    return await resolveEvent(ctx);
  } catch (error) {
    ErrorMonitoringService.captureError(error);
    return null;
  }
}

/**
 * Choose the config source from the first resolver that yields one, in
 * priority order: a shared workspace (v1 or v2), a layout by id, a layout by
 * name, a handed-off layout, the event's own panels, then the user's default
 * layout.
 *
 * @throws UnrecoverableError when a workspace is missing or its config is invalid
 * @throws RobotoUnauthorizedException when the user may not view the resolved resource
 */
async function resolveConfigSource(
  ctx: VisualizerInitContext,
  eventResolution: EventResolution | null,
): Promise<ConfigSource> {
  const resolvers = [
    () => resolveByWorkspaceId(ctx),
    () => resolveByLayoutId(ctx),
    () => resolveByLayoutName(ctx),
    () => resolveByLayoutFromNavigationState(ctx),
    () => Promise.resolve(resolveEventPanels(eventResolution)),
    () => resolveByDefaultLayout(ctx),
  ];

  for (const resolve of resolvers) {
    try {
      const result = await resolve();
      if (result !== null) {
        return result;
      }
    } catch (error) {
      if (
        error instanceof UnrecoverableError ||
        error instanceof RobotoUnauthorizedException
      ) {
        throw error;
      }

      // Log unexpected errors and fall through to the next resolver.
      ErrorMonitoringService.captureError(error);
    }
  }

  return null;
}

/** The URL's own datasources plus the event it named, resolved. */
interface ResolvedContributions {
  /**
   * The event the URL named, fetched or not. A restored workspace skips the
   * fetch, leaving `eventResolution` null, so this id is the only record that
   * the URL asked for it.
   */
  eventId: string | undefined;
  eventResolution: EventResolution | null;
  urlDatasources: Datasource[];
}

/**
 * Assemble the loader's result from the resolved config source and event. A
 * restored v1 workspace carries its own datasources; a v2 workspace's saved
 * datasources are merged ahead of the URL's. Both drop the event; every other
 * source loads the URL's datasources (including session datasources, which the
 * fetcher expands) and the event's together.
 */
function assembleInitData(
  ctx: VisualizerInitContext,
  configSource: ConfigSource,
  contributions: ResolvedContributions,
): VisualizerInitData {
  const { eventId, eventResolution, urlDatasources } = contributions;

  const droppedNotices: VisualizerNotice[] = [];
  if (ctx.workspaceId !== undefined && eventId) {
    droppedNotices.push(
      eventDroppedForWorkspaceNotice(ctx.workspaceId, eventId),
    );
  }

  if (
    configSource?.type === "layout" &&
    configSource.origin === "workspace-v2"
  ) {
    return {
      configSource,
      // Saved datasources first, so a file that both the workspace and the URL
      // name keeps its saved form.
      datasources: mergeDedupDatasources(
        configSource.datasources,
        urlDatasources,
      ),
      notices: [...configSource.notices, ...droppedNotices],
    };
  }

  if (configSource?.type === "workspace") {
    return {
      configSource,
      // The restored workspace carries its own datasources.
      datasources: [],
      // No sidebar view: the workspace decides what opens, and the URL's event
      // was never fetched, so there is nothing to show for it.
      notices: droppedNotices,
    };
  }

  return {
    configSource,
    datasources: mergeDedupDatasources(
      urlDatasources,
      eventDatasources(eventResolution),
    ),
    eventSidebar: eventSidebarFor(eventResolution),
    notices: eventNotices(eventResolution),
  };
}

/**
 * The banner shown when a shared workspace opens with an event in the URL:
 * the workspace restores its own data, so the event is not applied. Names the
 * workspace when its id is known.
 */
function eventDroppedForWorkspaceNotice(
  workspaceId: string | undefined,
  eventId: string,
): VisualizerNotice {
  const workspace = workspaceId
    ? `workspace '${workspaceId}'`
    : "a saved workspace";
  return {
    name: "EventDroppedForWorkspaceNotice",
    title: "Event not loaded",
    message: `The URL includes both ${workspace} and event '${eventId}' references, but they are mutually exclusive. The workspace opened with its saved panels and data.`,
    severity: "info",
  };
}

/**
 * Read the datasources named directly in the URL, collapsing duplicates. Each
 * `?session=` becomes a session datasource — a reference the fetcher expands to
 * the session's files and record; the loader does no fetching for it.
 */
function readUrlDatasources(searchParams: URLSearchParams): Datasource[] {
  const datasetIds = [...new Set(searchParams.getAll(SearchParams.DATASET))];
  const fileIds = [...new Set(searchParams.getAll(SearchParams.FILE))];
  const sessionIds = [...new Set(searchParams.getAll(SearchParams.SESSION))];

  return [
    ...datasetIds.map((datasetId) => ({
      type: "dataset" as const,
      value: { datasetId },
    })),
    ...fileIds.map((fileId) => ({
      type: "file" as const,
      value: { fileId },
    })),
    ...sessionIds.map((sessionId) => ({
      type: "session" as const,
      value: { sessionId },
    })),
  ];
}

/**
 * Merge the lists in order, keeping the first occurrence of each dataset, file,
 * or session id.
 *
 * This settles only the ids a URL arrives with. Adding a file to a workspace
 * already on screen goes through `useAddFilesToWorkspace`, which keeps the
 * datasources that are already there.
 */
function mergeDedupDatasources(...lists: Datasource[][]): Datasource[] {
  const seen = new Set<string>();
  const merged: Datasource[] = [];

  for (const datasource of lists.flat()) {
    const key = datasourceKey(datasource);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    merged.push(datasource);
  }

  return merged;
}

function datasourceKey(datasource: Datasource): string {
  switch (datasource.type) {
    case "dataset":
      return `dataset:${datasource.value.datasetId}`;
    case "file":
      return `file:${datasource.value.fileId}`;
    case "session":
      return `session:${datasource.value.sessionId}`;
  }
}

/**
 * react-router shouldRevalidate for the /visualize route.
 *
 * @link https://reactrouter.com/6.30.3/route/should-revalidate
 */
export function shouldRevalidate() {
  // The loader reads the URL (`?event=`, `?dataset=`, `?file=`, `?session=`,
  // `?layout=`, `?layoutId=`, `?t=`) only to seed the initial workspace
  // state. URL changes after that (e.g. adding files to the workspace
  // rewrites `?dataset=`/`?file=`; a v2 workspace's `?t=` redirect lands
  // before any render) are read reactively by the workspace, so revalidation
  // is never needed.
  return false;
}

/**
 * Retrieve the loader data for the /visualize route.
 */
export function useWorkspaceLoaderData(): VisualizerInitData {
  return useLoaderData<VisualizerInitData>();
}
